import { CheckCircleOutlined, CloseCircleOutlined, DeleteOutlined, ReloadOutlined } from '@ant-design/icons';
import { Alert, Button, Card, Col, Empty, Modal, Row, Space, Statistic, Table, Tag, Typography, message } from 'antd';
import React, { useEffect, useState, useCallback } from 'react';
import { apiService } from '../services/api';

const { Title, Text } = Typography;

const ProxyCreationLogs = ({ clientLogin = null, autoRefresh = false, onLogsUpdate = null }) => {
  const [logs, setLogs] = useState([]);
  const [loading, setLoading] = useState(false);
  const [stats, setStats] = useState({ total: 0, success: 0, failed: 0 });

  const fetchLogs = useCallback(async () => {
    setLoading(true);
    try {
      const params = { limit: 20 };
      if (clientLogin) {
        params.client_login = clientLogin;
      }

      const response = await apiService.getProxyCreationLogs(params);
      
      if (response && response.data && response.data.logs) {
        setLogs(response.data.logs);
        
        // Подсчитываем статистику
        const successCount = response.data.logs.filter(log => log.status === 'success').length;
        const failedCount = response.data.logs.filter(log => log.status === 'error').length;
        
        setStats({
          total: response.data.logs.length,
          success: successCount,
          failed: failedCount
        });
        
        if (onLogsUpdate) {
          onLogsUpdate(response.data.logs);
        }
      }
    } catch (error) {
      // Ошибка загрузки логов создания прокси
    } finally {
      setLoading(false);
    }
  }, [clientLogin, onLogsUpdate]);

  const handleClearLogs = () => {
    Modal.confirm({
      title: 'Очистить логи создания прокси',
      content: 'Вы уверены, что хотите очистить все логи создания прокси? Это действие нельзя отменить. Будет создана резервная копия.',
      okText: 'Очистить',
      okType: 'danger',
      cancelText: 'Отмена',
      onOk: async () => {
        try {
          setLoading(true);
          const response = await apiService.clearProxyCreationLogs();
          
          if (response && response.data && response.data.cleared) {
            message.success('Логи успешно очищены');
            // Обновляем логи после очистки
            await fetchLogs();
          } else {
            message.warning(response?.data?.message || 'Не удалось очистить логи');
          }
        } catch (error) {
          message.error('Ошибка при очистке логов');
        } finally {
          setLoading(false);
        }
      }
    });
  };

  useEffect(() => {
    fetchLogs();
    
    // Автообновление каждые 10 секунд если включено
    let interval;
    if (autoRefresh) {
      interval = setInterval(fetchLogs, 10000);
    }
    
    return () => {
      if (interval) {
        clearInterval(interval);
      }
    };
  }, [clientLogin, autoRefresh, fetchLogs]);

  const getStatusColor = (status) => {
    switch (status) {
      case 'success':
        return 'success';
      case 'error':
        return 'error';
      default:
        return 'default';
    }
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case 'success':
        return <CheckCircleOutlined style={{ color: '#52c41a' }} />;
      case 'error':
        return <CloseCircleOutlined style={{ color: '#ff4d4f' }} />;
      default:
        return null;
    }
  };

  const columns = [
    {
      title: 'Время',
      dataIndex: 'timestamp',
      key: 'timestamp',
      width: 180,
      render: (text) => <Text>{text}</Text>,
    },
    {
      title: 'Клиент',
      dataIndex: 'client',
      key: 'client',
      width: 150,
      render: (text) => <Text strong>{text}</Text>,
    },
    {
      title: 'Тариф',
      dataIndex: 'tariff',
      key: 'tariff',
      width: 150,
    },
    {
      title: 'Статус',
      dataIndex: 'status',
      key: 'status',
      width: 100,
      render: (status) => (
        <Tag color={getStatusColor(status)} icon={getStatusIcon(status)}>
          {status === 'success' ? 'Успешно' : status === 'error' ? 'Ошибка' : 'Неизвестно'}
        </Tag>
      ),
    },
    {
      title: 'ID Прокси',
      dataIndex: 'proxy_id',
      key: 'proxy_id',
      width: 100,
      render: (text) => text ? <Text code>{text}</Text> : '-',
    },
    {
      title: 'Порт',
      dataIndex: 'port',
      key: 'port',
      width: 100,
      render: (text) => text ? <Text code>{text}</Text> : '-',
    },
    {
      title: 'Сервер',
      dataIndex: 'server',
      key: 'server',
      width: 150,
      render: (text, record) => {
        if (text) {
          return (
            <Text>
              {text}
              {record.server_id && <Text type="secondary"> (ID: {record.server_id})</Text>}
            </Text>
          );
        }
        return '-';
      },
    },
    {
      title: 'Подключение',
      dataIndex: 'connection',
      key: 'connection',
      width: 150,
      render: (text, record) => {
        if (text) {
          return (
            <Text>
              {text}
              {record.connection_id && <Text type="secondary"> (ID: {record.connection_id})</Text>}
            </Text>
          );
        }
        return '-';
      },
    },
    {
      title: 'Модем',
      dataIndex: 'modem',
      key: 'modem',
      width: 120,
      render: (text) => text || '-',
    },
    {
      title: 'Скорость',
      dataIndex: 'speed',
      key: 'speed',
      width: 100,
      render: (text) => text ? `${text} Мб/с` : '-',
    },
    {
      title: 'Загрузка',
      dataIndex: 'load_after',
      key: 'load_after',
      width: 100,
      render: (text) => text ? `${text}%` : '-',
    },
    {
      title: 'Причина / Детали',
      dataIndex: 'error',
      key: 'error',
      render: (error, record) => {
        if (error) {
          return <Text type="danger">{error}</Text>;
        }
        if (record.status === 'success') {
          // * Показываем подробную информацию для успешных записей
          const details = [];
          if (record.server) {
            details.push(`Сервер: ${record.server}`);
          }
          if (record.connection) {
            details.push(`Подключение: ${record.connection}`);
          }
          if (record.modem) {
            details.push(`Модем: ${record.modem}`);
          }
          if (record.port) {
            details.push(`Порт: ${record.port}`);
          }
          if (record.speed) {
            details.push(`Скорость: ${record.speed} Мб/с`);
          }
          if (record.load_after) {
            details.push(`Загрузка: ${record.load_after}%`);
          }
          return details.length > 0 ? (
            <Text type="success">
              {details.join(', ')}
            </Text>
          ) : '-';
        }
        if (record.summary) {
          return (
            <Text type="secondary">
              Создано: {record.summary.created}/{record.summary.requested} 
              ({record.summary.success_rate}% за {record.summary.time})
            </Text>
          );
        }
        return '-';
      },
    },
  ];

  return (
    <Card 
      title={
        <Space>
          <Title level={5} style={{ margin: 0 }}>
            📋 Логи создания прокси
          </Title>
          <Button 
            type="text" 
            icon={<ReloadOutlined />} 
            onClick={fetchLogs}
            loading={loading}
          >
            Обновить
          </Button>
          <Button 
            type="text" 
            danger
            icon={<DeleteOutlined />} 
            onClick={handleClearLogs}
            loading={loading}
          >
            Очистить логи
          </Button>
        </Space>
      }
      style={{ marginBottom: 24 }}
    >
      {/* Статистика */}
      <Row gutter={16} style={{ marginBottom: 16 }}>
        <Col span={8}>
          <Statistic 
            title="Всего записей" 
            value={stats.total} 
            prefix="📊"
          />
        </Col>
        <Col span={8}>
          <Statistic 
            title="Успешно" 
            value={stats.success} 
            valueStyle={{ color: '#52c41a' }}
            prefix={<CheckCircleOutlined />}
          />
        </Col>
        <Col span={8}>
          <Statistic 
            title="Ошибок" 
            value={stats.failed} 
            valueStyle={{ color: '#ff4d4f' }}
            prefix={<CloseCircleOutlined />}
          />
        </Col>
      </Row>

      {/* Таблица логов */}
      {logs.length === 0 && !loading ? (
        <Empty 
          description="Нет логов создания прокси"
          image={Empty.PRESENTED_IMAGE_SIMPLE}
        />
      ) : (
        <Table
          columns={columns}
          dataSource={logs.map((log, index) => ({ ...log, key: index }))}
          loading={loading}
          size="small"
          pagination={{
            pageSize: 10,
            showSizeChanger: true,
            showTotal: (total) => `Всего: ${total} записей`,
          }}
          scroll={{ x: 1200 }}
        />
      )}

      {autoRefresh && (
        <Alert
          message="Автообновление включено"
          description="Логи обновляются каждые 10 секунд"
          type="info"
          showIcon
          style={{ marginTop: 16 }}
        />
      )}
    </Card>
  );
};

export default ProxyCreationLogs;

